import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Shield, Check, X, Clock, UserX, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTS = [
  { value: 'reader',      label: 'Lecture seule' },
  { value: 'contributor', label: 'Contributeur' },
  { value: 'admin',       label: 'Administrateur' },
];

const STATUS_BADGE = {
  pending:  { label: 'En attente',  icon: Clock,  cls: 'bg-amber-100 text-amber-700' },
  active:   { label: 'Actif',       icon: Check,  cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Refusé',      icon: X,      cls: 'bg-red-100 text-red-700' },
  disabled: { label: 'Désactivé',   icon: UserX,  cls: 'bg-stone-100 text-stone-500' },
};

export const AdminUsersPage = () => {
  const { user: me } = useAuth();
  const navigate     = useNavigate();
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(null); // id en cours

  useEffect(() => {
    if (me && me.role !== 'admin') { navigate('/dashboard'); return; }
    fetchUsers();
  }, [me]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/users`, { withCredentials: true });
      setUsers(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const updateUser = async (id, patch) => {
    setSaving(id);
    try {
      const { data } = await axios.patch(`${API_URL}/api/auth/users/${id}`, patch, { withCredentials: true });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...data } : u));
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const pendingCount = users.filter(u => u.status === 'pending').length;

  if (me?.role !== 'admin') return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-forest mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Gestion des utilisateurs
          </h1>
          <p className="text-stone-500">
            {pendingCount > 0
              ? <span className="text-amber-600 font-medium">{pendingCount} inscription{pendingCount > 1 ? 's' : ''} en attente de validation</span>
              : 'Tous les comptes sont traités'}
          </p>
        </div>
        <Button variant="ghost" onClick={fetchUsers} className="text-stone-500 hover:text-forest">
          <RefreshCw size={17} className="mr-2" /> Actualiser
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-stone-400">Chargement…</div>
      ) : (
        <div className="space-y-3">
          {/* En attente en premier */}
          {[...users].sort((a, b) => {
            const order = { pending: 0, active: 1, contributor: 1, admin: 1, rejected: 2, disabled: 3 };
            return (order[a.status] ?? 1) - (order[b.status] ?? 1);
          }).map(u => {
            const badge  = STATUS_BADGE[u.status] || STATUS_BADGE.active;
            const BadgeIcon = badge.icon;
            const isSaving  = saving === u.id;
            const isMe      = u.id === me.id;

            return (
              <div key={u.id}
                className={`bg-white border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
                  u.status === 'pending' ? 'border-amber-300 bg-amber-50/30' : 'border-parchment-dark'
                }`}>

                {/* Identité */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-stone-900">{u.prenom} {u.nom}</span>
                    {isMe && <span className="text-xs bg-forest text-white px-2 py-0.5 rounded-full">Moi</span>}
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>
                      <BadgeIcon size={11} /> {badge.label}
                    </span>
                  </div>
                  <p className="text-sm text-stone-500 mt-0.5">{u.email}</p>
                  <p className="text-xs text-stone-400">Login : {u.login} · Inscrit le {new Date(u.created_at).toLocaleDateString('fr-FR')}</p>
                </div>

                {/* Actions */}
                {!isMe && (
                  <div className="flex flex-wrap items-center gap-2 shrink-0">

                    {/* Validation inscription */}
                    {u.status === 'pending' && (
                      <>
                        <Button size="sm" disabled={isSaving}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => updateUser(u.id, { status: 'active' })}>
                          <Check size={14} className="mr-1" /> Valider
                        </Button>
                        <Button size="sm" variant="outline" disabled={isSaving}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => updateUser(u.id, { status: 'rejected' })}>
                          <X size={14} className="mr-1" /> Refuser
                        </Button>
                      </>
                    )}

                    {/* Rôle */}
                    {u.status !== 'pending' && (
                      <select
                        value={u.role}
                        disabled={isSaving}
                        onChange={(e) => updateUser(u.id, { role: e.target.value })}
                        className="text-sm border border-parchment-dark px-2 py-1.5 bg-white text-stone-700 focus:outline-none focus:ring-1 focus:ring-forest"
                      >
                        {ROLE_OPTS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    )}

                    {/* Désactiver / réactiver */}
                    {u.status === 'active' && (
                      <Button size="sm" variant="ghost" disabled={isSaving}
                        className="text-stone-400 hover:text-red-500 text-xs"
                        onClick={() => updateUser(u.id, { status: 'disabled' })}>
                        <UserX size={14} className="mr-1" /> Désactiver
                      </Button>
                    )}
                    {u.status === 'disabled' && (
                      <Button size="sm" variant="ghost" disabled={isSaving}
                        className="text-stone-400 hover:text-green-600 text-xs"
                        onClick={() => updateUser(u.id, { status: 'active' })}>
                        <Check size={14} className="mr-1" /> Réactiver
                      </Button>
                    )}
                    {u.status === 'rejected' && (
                      <Button size="sm" variant="ghost" disabled={isSaving}
                        className="text-stone-400 hover:text-green-600 text-xs"
                        onClick={() => updateUser(u.id, { status: 'active' })}>
                        <Check size={14} className="mr-1" /> Valider quand même
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
